import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListReportSubscriptionsResult } from '@whinerp/client';
import { formatDateTime } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Subscribe to a report (F-099): frequency, time, recipients (colleagues or
 * not), and whether to include the rows. The figures travel in the email body.
 */

const PRESETS = [
  { key: 'daily', cron: '0 8 * * *' },
  { key: 'weekdays', cron: '0 8 * * 1-5' },
  { key: 'weekly', cron: '0 8 * * 1' },
  { key: 'monthly', cron: '0 8 1 * *' },
] as const;
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

export function ReportSubscriptionsPanel({ reportId }: { reportId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-099');
  const { can } = useSession();
  const [preset, setPreset] = useState<string>('weekly');
  const [recipients, setRecipients] = useState('');
  const [includeRows, setIncludeRows] = useState(false);
  const list = useQuery<ListReportSubscriptionsResult, ApiError>({
    queryKey: ['report-subscriptions', reportId],
    queryFn: () => api.listReportSubscriptions({ path: { reportId } }),
    enabled: enabled.enabled,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['report-subscriptions', reportId] });
  const save = useMutation({
    mutationFn: () =>
      api.saveReportSubscription({
        path: { reportId },
        body: {
          subscriptionId: null,
          cron: PRESETS.find((option) => option.key === preset)?.cron ?? '0 8 * * 1',
          recipients: recipients.split(/[,;\s]+/).filter(Boolean),
          includeRows,
          active: true,
        },
      }),
    onSuccess: async () => {
      setRecipients('');
      await refresh();
    },
  });
  const send = useMutation({
    mutationFn: (subscriptionId: string) => api.sendReportSubscriptionNow({ path: { reportId, subscriptionId }, body: {} }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (subscriptionId: string) => api.deleteReportSubscription({ path: { reportId, subscriptionId }, body: {} }),
    onSuccess: refresh,
  });
  if (!enabled.enabled || !can('report.manage')) return null;
  const refusal = save.error ?? send.error ?? remove.error;
  return (
    <Panel data-region="report-subscriptions">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('subscribe.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('subscribe.help')}</p>
        <ul className="flex flex-col">
          {(list.data?.subscriptions ?? []).map((sub) => (
            <li key={sub.subscriptionId} data-subscription={sub.subscriptionId} className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0">
              <span className="text-dense text-ink">{sub.recipients.join(', ')}</span>
              <code className="text-meta text-ink-600">{sub.cron}</code>
              <span className="text-meta text-ink-meta">
                {sub.nextRunAt ? t('subscribe.next', { values: { at: formatDateTime(sub.nextRunAt, format) } }) : ''}
              </span>
              {sub.lastDetail ? <span data-subscription-last className="text-meta text-ink-meta">{sub.lastDetail}</span> : null}
              <span className="ms-auto flex gap-2">
                <Button intent="secondary" size="small" data-subscription-send disabled={send.isPending} onClick={() => send.mutate(sub.subscriptionId)}>
                  {t('subscribe.sendNow')}
                </Button>
                <Button intent="ghost" size="small" data-subscription-delete disabled={remove.isPending} onClick={() => remove.mutate(sub.subscriptionId)}>
                  {t('subscribe.stop')}
                </Button>
              </span>
            </li>
          ))}
        </ul>
        {send.data ? <p data-subscription-sent className="text-dense text-ink">{send.data.detail}</p> : null}
        <div className="flex flex-wrap items-center gap-2">
          <select data-subscribe-frequency aria-label={t('subscribe.frequency')} className={INPUT} value={preset} onChange={(e) => setPreset(e.target.value)}>
            {PRESETS.map((option) => (
              <option key={option.key} value={option.key}>{t(`subscribe.preset.${option.key}`)}</option>
            ))}
          </select>
          <input
            data-subscribe-recipients
            aria-label={t('subscribe.recipients')}
            placeholder={t('subscribe.recipients')}
            className={`${INPUT} min-w-0 flex-1`}
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
          />
          <label className="flex items-center gap-1 text-dense text-ink">
            <input type="checkbox" data-subscribe-rows checked={includeRows} onChange={(e) => setIncludeRows(e.target.checked)} />
            {t('subscribe.includeRows')}
          </label>
          <Button intent="primary" size="small" data-subscribe-save disabled={save.isPending} onClick={() => save.mutate()}>
            {t('subscribe.save')}
          </Button>
        </div>
        {refusal ? <p data-subscribe-refusal className="text-dense text-danger-text">{errorText(refusal)}</p> : null}
      </div>
    </Panel>
  );
}
